import { z } from "zod";

/**
 * Shared DTO cho đăng nhập bằng Email-OTP 6 số (US-E13.F0-0082-S28 / @UC0002).
 * Một nguồn sự thật cho api (validate biên) và mobile (gọi + map LoginResult),
 * tránh drift (DRY). C5: schema validate tại biên tái dùng được.
 *
 * B5: chỉ email-OTP — KHÔNG mật khẩu, KHÔNG SMS. Mã đầu tiên tự tạo tài khoản
 * (find-or-create server-side). Enumeration-safe: request LUÔN 200.
 * Invariant: code hash sha256 (raw chỉ ở outbox), timingSafeEqual, one-time (usedAt),
 * TTL ngắn (~10min), lockout sau 5 lần sai (low-entropy 6 số).
 */

/** Độ dài mã OTP (6 chữ số). */
export const EMAIL_OTP_CODE_LENGTH = 6;

/** Body cho POST /v1/auth/email/otp/request (AC1/AC2). Chuẩn hoá email tại biên. */
export const emailOtpRequestSchema = z
  .object({
    // trim + lowercase để khớp citext @unique nhất quán với register/login.
    email: z.string().trim().toLowerCase().pipe(z.string().email()),
  })
  .strict();

export type EmailOtpRequest = z.infer<typeof emailOtpRequestSchema>;

/** Body cho POST /v1/auth/email/otp/verify (AC3). code = CHÍNH XÁC 6 chữ số. */
export const emailOtpVerifySchema = z
  .object({
    email: z.string().trim().toLowerCase().pipe(z.string().email()),
    code: z
      .string()
      .length(EMAIL_OTP_CODE_LENGTH)
      .regex(/^\d{6}$/, { message: "Mã OTP phải là 6 chữ số" }),
  })
  .strict();

export type EmailOtpVerifyRequest = z.infer<typeof emailOtpVerifySchema>;

/**
 * Response request-otp — LUÔN 200 giống hệt (enumeration-safe, không lộ email tồn tại).
 */
export interface EmailOtpRequestResponse {
  status: "sent";
}

/**
 * Response verify-otp thành công. C3: TUYỆT ĐỐI không chứa secret/hash/code.
 * `token` = access JWT (mobile map LoginResult.accessToken); refreshToken opaque;
 * emailVerified: OTP xác minh quyền sở hữu email ⇒ luôn true. isNewUser: mã đầu auto-tạo tài khoản.
 */
export interface EmailOtpVerifyResponse {
  token: string;
  refreshToken: string;
  userId: string;
  emailVerified: boolean;
  isNewUser: boolean;
}

/** Hằng response request (always-200) — dùng chung api ↔ mobile. */
export const EMAIL_OTP_SENT_STATUS = "sent" as const;

/**
 * Hằng message/code lỗi verify dùng chung (DRY giữa api & mobile).
 * Message enumeration/entropy-safe: mã sai/hết hạn/đã dùng đều generic (không tiết lộ khác biệt).
 */
export const EMAIL_OTP_INVALID_MESSAGE = "Mã xác thực không đúng hoặc đã hết hạn";
export const EMAIL_OTP_LOCKED_MESSAGE = "Nhập sai quá nhiều lần. Thử lại sau 5 phút.";

/** Machine code ổn định, gắn vào RFC7807 problem+json `code` để mobile map. */
export const EMAIL_OTP_INVALID_CODE = "OTP_INVALID";
export const EMAIL_OTP_LOCKED_CODE = "OTP_LOCKED";
